import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
    catchError,
    combineLatestWith,
    concatMap,
    exhaustMap,
    map,
    mergeMap,
    of,
    switchMap,
    withLatestFrom,
} from 'rxjs';
import { PlayerNotificationsResponse } from '../../../profile/models/currentPlayer.interface';
import { ProfileNotificationType } from '../../../profile/models/enums/profile.enum';
import { InvitationService } from '../../../profile/services/invitation.service';
import { clearInvitation } from '../../../profile/store/actions/profileGeneral.action';
import { profileGetAllTeams } from '../../../profile/store/actions/profileTeams.action';
import { selectCurrentPlayer } from '../../../profile/store/selectors/profileGeneral.selector';
import { selectProfileTeams } from '../../../profile/store/selectors/profileTeams.selector';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import { GUID_EMPTY, MAX_TEAMS_COUNT, NOTIFICATIONS } from '../../../shared/constants';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import { AppState } from '../../../shared/store/appState';
import { profileGetTeamInfo } from '../../../team/store/actions/team.action';
import { TournamentParticipantsActions } from '../../../tournament/store/actions/actions-type.action';
import { ProfileNotificationsService } from '../../services/profile-notifications.service';
import { ProfileNotificationsActions } from '../actions/actions-type.action';
import { RewardsService } from 'src/app/reward/services/reward.service';
import { MyRecord } from '../../models/notification.interface';

@Injectable()
export class ProfileNotificationsEffect {
    private actions$ = inject(Actions);
    private store = inject<Store<AppState>>(Store);
    private profileNotificationsService = inject(ProfileNotificationsService);
    private invitationService = inject(InvitationService);
    private notificationsService = inject(NotificationsService);
    private rewardsService = inject(RewardsService);

    getAllCurrentPlayerNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileNotificationsActions.fetchPlayerNotifications),
            switchMap((action) =>
                this.profileNotificationsService
                    .getAll(
                        action.payload.playerId,
                        action.payload.page,
                        action.payload.pageSize
                    )
                    .pipe(
                        map((response: PlayerNotificationsResponse) =>
                            ProfileNotificationsActions.fetchPlayerNotificationsSuccess(
                                {
                                    payload: {
                                        response,
                                        patchData:
                                            action.payload.patchData ?? false,
                                    },
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileNotificationsActions.fetchPlayerNotificationsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    markAsReadPlayerNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileNotificationsActions.markAsReadNotification),
            exhaustMap((action) =>
                this.profileNotificationsService
                    .markAsRead(action.payload.notificationIds)
                    .pipe(
                        mergeMap(() => [
                            ProfileNotificationsActions.markAsReadNotificationSuccess(
                                {
                                    payload: {
                                        notificationIds:
                                            action.payload.notificationIds,
                                    },
                                }
                            ),
                        ]),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileNotificationsActions.markAsReadNotificationFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    deletePlayerNotification$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileNotificationsActions.deleteNotification),
            exhaustMap((action) =>
                this.profileNotificationsService
                    .deleteNotification(action.payload.id)
                    .pipe(
                        map(() =>
                            ProfileNotificationsActions.deleteNotificationSuccess(
                                {
                                    payload: action.payload,
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.SOMETHING_WENT_WRONG,
                                type: SnackBarType.Error,
                            });
                            return of(
                                ProfileNotificationsActions.deleteNotificationFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    )
            )
        )
    );

    updateInvitationNotificationStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ProfileNotificationsActions.updateInvitationNotificationStatus
            ),
            withLatestFrom(
                this.store.select(selectProfileTeams),
                this.store.select(selectCurrentPlayer)
            ),
            exhaustMap(([action, profileTeams, currentPlayer]) => {
                const { requestData, options } = action.payload;
                if (
                    requestData.notification.notificationType ===
                    ProfileNotificationType.TeamInvitation &&
                    profileTeams.length === MAX_TEAMS_COUNT
                ) {
                    this.notificationsService.openSnackBar({
                        title: 'Team limit reached',
                        message: NOTIFICATIONS.TEAM_LIMIT_REACHED,
                        type: SnackBarType.Error,
                    });

                    return [
                        ProfileNotificationsActions.updateInvitationNotificationStatusFailure(
                            {
                                payload: NOTIFICATIONS.TEAM_LIMIT_REACHED,
                            }
                        ),
                    ];
                }

                return this.invitationService
                    .updateStatus(
                        requestData.invitationId,
                        requestData.status,
                        options
                    )
                    .pipe(
                        concatMap(() => {
                            const additionalSideEffects: any[] =
                                // const additionalSideEffects: TypedAction<string>[] =
                                [
                                    clearInvitation({
                                        payload: {
                                            invitationId:
                                                requestData.invitationId,
                                        },
                                    }),
                                ];

                            if (
                                requestData.shouldUpdateParticipants &&
                                requestData.targetId
                            ) {
                                additionalSideEffects.push(
                                    // fetchTournamentParticipants({
                                    //     tournamentId: requestData.targetId,
                                    // })
                                );
                            }
                            if (
                                requestData.shouldUpdateTeam &&
                                requestData.targetId
                            ) {
                                additionalSideEffects.push(
                                    profileGetTeamInfo({
                                        payload: requestData.targetId,
                                    }),
                                    profileGetAllTeams({
                                        payload: {
                                            profileId:
                                                requestData.notification
                                                    .playerId,
                                        },
                                    })
                                );
                            }

                            if (
                                requestData.shouldUpdatePlayerTournamentsTeams &&
                                requestData.targetId
                            ) {
                                additionalSideEffects.push(
                                    TournamentParticipantsActions.fetchJoiningTeamTournament(
                                        {
                                            payload: {
                                                playerId:
                                                    currentPlayer.profile
                                                        .playerId,
                                                tournamentId:
                                                    requestData.targetId,
                                            },
                                        }
                                    )
                                );
                            }
                            return [
                                ProfileNotificationsActions.deleteNotification({
                                    payload: requestData.notification,
                                }),
                                ...additionalSideEffects,
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            const additionalSideEffects: any[] =
                                // const additionalSideEffects: TypedAction<string>[] =
                                [];

                            if (
                                [
                                    'InvitationTargetDoesNotExist',
                                    'InvitationCanceled',
                                    'InvitationExpired',
                                ].filter((x) => x === response.error.code)
                                    .length
                            ) {
                                additionalSideEffects.push(
                                    ProfileNotificationsActions.deleteNotification(
                                        {
                                            payload:
                                                action.payload.requestData
                                                    .notification,
                                        }
                                    )
                                );
                            }

                            return [
                                ProfileNotificationsActions.updateInvitationNotificationStatusFailure(
                                    {
                                        payload: response.error,
                                    }
                                ),
                                ...additionalSideEffects,
                            ];
                        })
                    );
            })
        )
    );

    updateRewardNotificationDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ProfileNotificationsActions.updateNotificationDetails
            ),
            withLatestFrom(
                this.store.select(selectCurrentPlayer)
            ),
            exhaustMap(([action, currentPlayer]) => {
                const payload = action.payload;
                return this.rewardsService
                    .RewardsClaim(
                        currentPlayer.profile.playerId,
                        action.payload.details['RewardId'] ?? ''
                    )
                    .pipe(
                        concatMap(() => {
                            const details: MyRecord = { ["RewardId"]: GUID_EMPTY };
                            return this.profileNotificationsService.updateNotification(
                                payload.id,
                                details
                            ).pipe(combineLatestWith(of(payload)));
                        }),
                        concatMap(() => {
                            const additionalSideEffects: any[] =
                                [
                                    ProfileNotificationsActions.updateNotificationParameterSuccess(
                                        {
                                            payload: action.payload,
                                        }
                                    )
                                ];
                            return additionalSideEffects;
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.SOMETHING_WENT_WRONG,
                                type: SnackBarType.Error,
                            });
                            return of(
                                ProfileNotificationsActions.updateNotificationParameteFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                    );
            })
        )
    );
}
